import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import CroppedImage from './CroppedImage';
import StepNavigation from './StepNavigation';
import PaymentSection from './PaymentSection';
import QuantityInput from './QuantityInput';
import { findProductConfig, getProductRatio, calculateAspectPadding } from '../utils/productUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


//Cart Section Component  
function CartSection({ items, printSizes, shippingOptions, onRemoveItem, onOrderComplete, activeTab, setActiveTab, onUpdateItem, uploadedImages, onShowOrderSummary, orderSummaryData, setEditingImage, onUpdateQuantity }) {
  const [cartTotal, setCartTotal] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [sortBy, setSortBy] = useState('added'); // 'added', 'filename-asc', 'filename-desc'
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  // Helper function to validate base64 thumbnail
  const isValidThumbnail = (thumbnail) => {
    if (!thumbnail || typeof thumbnail !== 'string') return false;
    // Must be at least 100 chars and not be a known corrupted string
    if (thumbnail.length < 100) return false;
    // Filter out known corrupted base64 strings
    const corruptedPatterns = ['2Q==', 'Z:1', '9k=', 'iVBOR', '/9j/'];
    if (corruptedPatterns.some(pattern => thumbnail === pattern || thumbnail.startsWith(pattern) && thumbnail.length < 200)) {
      return false;
    }
    return true;
  };

  // Group cart items by image filename - MOVED TO TOP
  const groupedItems = React.useMemo(() => {
    const groups = {};
    items.forEach(item => {
      const filename = item.filename;
      if (!groups[filename]) {
        // Find the uploadedImage using filename
        const uploadedImage = uploadedImages.find(img => img.filename === filename);
        let thumbnail = null;
        
        // Try to get a thumbnail from uploadedImages
        // Use original thumbnail for group header (not cropped)
        if (uploadedImage) {
          thumbnail = isValidThumbnail(uploadedImage.thumbnail) 
            ? uploadedImage.thumbnail 
            : (isValidThumbnail(uploadedImage.image_data) ? uploadedImage.image_data : null);
        }
        
        groups[filename] = {
          filename,
          thumbnail,
          uploadedImage, // Store reference to uploaded image for accessing cropped thumbnails
          items: [],
          totalItems: 0,
          totalPrice: 0
        };
      }
      groups[filename].items.push(item);
      groups[filename].totalItems += item.quantity;
      
      // Calculate price from cartTotal if available
      // Use shared utility to find product config by SKU or size
      const { config: productConfig, key: productKey } = findProductConfig(item, printSizes);
      const calculatedItem = cartTotal?.items?.find(
        calcItem => calcItem.filename === item.filename && (calcItem.sku === productKey || calcItem.size === item.size)
      );
      const itemPrice = calculatedItem 
        ? calculatedItem.total_price 
        : (productConfig?.price || 299) * item.quantity;
      groups[filename].totalPrice += itemPrice;
    });
    return Object.values(groups);
  }, [items, cartTotal, printSizes, uploadedImages]);

  // Sort grouped items based on selected sort option
  const sortedGroupedItems = useMemo(() => {
    if (!groupedItems || groupedItems.length === 0) return [];
    
    const sorted = [...groupedItems];
    switch (sortBy) {
      case 'filename-asc':
        sorted.sort((a, b) => (a.filename || '').localeCompare(b.filename || ''));
        break;
      case 'filename-desc':
        sorted.sort((a, b) => (b.filename || '').localeCompare(a.filename || ''));
        break;
      case 'added':
      default:
        // Keep original order (order added to cart)
        break;
    }
    return sorted;
  }, [groupedItems, sortBy]);

  // Collapsible state for each image group - MOVED TO TOP
  const [expandedGroups, setExpandedGroups] = React.useState(() => {
    // Start with all groups collapsed (closed)
    const initial = {};
    groupedItems.forEach(group => {
      initial[group.filename] = false;
    });
    return initial;
  });

  const toggleGroup = (filename) => {
    setExpandedGroups(prev => ({
      ...prev,
      [filename]: !prev[filename]
    }));
  };

  useEffect(() => {
    if (items.length > 0) {
      calculateCartTotal();
    } else {
      setCartTotal(null);
    }
  }, [items]);
  
  const enrichCartItemsWithImageData = (cartItems) => {
    return cartItems.map(item => {
      // Find the corresponding uploaded image by filename or image_id
      const uploadedImage = uploadedImages.find(img => 
        img.filename === item.filename || img.id === item.image_id
      );
      
      if (!uploadedImage) {
        console.warn(`No uploaded image found for cart item: ${item.filename}`);
        return item;
      }
      
      // Get the ratio from printSizes configuration using shared utility
      const { config: sizeConfig } = findProductConfig(item, printSizes);
      const ratio = getProductRatio(sizeConfig);
      
      return {
        ...item,
        image_data: uploadedImage.image_data || uploadedImage.preview,
        thumbnail: uploadedImage.thumbnail, // Use backend thumbnail (600x600)
        image_id: uploadedImage.id,
        ratio: ratio // Include ratio for order display
      };
    });
  };

  const calculateCartTotal = async () => {
    // Reset cart total first to prevent stale data
    setCartTotal(null);
    
    try {
      console.log('Calculating cart total for items:', items.length);
      console.log('Item details:', items.map(i => ({ id: i.id, size: i.size, qty: i.quantity })));
      
      // Cart should NOT include attributes - only base prices + bulk discounts
      // Attributes are optional extras added at checkout
      const enrichedItems = enrichCartItemsWithImageData(items).map(item => ({
        ...item,
        paper_type: 'M', // Default matte (no surcharge)
        border: false // No border (no surcharge)
      }));
      
      console.log('Cart calculation (base prices only):', enrichedItems.length);
      
      const response = await axios.post('/cart/calculate', enrichedItems);
      console.log('Cart total response:', response.data);
      console.log('Backend subtotal (cents):', response.data.subtotal);
      console.log('Display subtotal (dollars):', (response.data.subtotal / 100).toFixed(2));
      setCartTotal(response.data);
    } catch (error) {
      console.error('Failed to calculate cart total:', error);
      console.error('Error details:', error.response?.data);
      setCartTotal(null);
    }
  };
  
  const handleApplyCoupon = async () => {
    console.log('Applying coupon:', couponCode);
    
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    try {
      // Calculate subtotal from cartTotal or manually from items
      let subtotal = cartTotal?.subtotal || 0;
      
      // If no cartTotal, calculate manually from items
      if (subtotal === 0 && items.length > 0) {
        subtotal = items.reduce((sum, item) => {
          const { config: product } = findProductConfig(item, printSizes);
          return sum + ((product?.price || 0) * item.quantity);
        }, 0);
      }
      
      console.log('Validating coupon with subtotal:', subtotal, 'cartTotal:', cartTotal);
      
      const response = await axios.post('/cart/validate-coupon', {
        code: couponCode,
        cart_total: subtotal
      });
      
      console.log('Coupon response:', response.data);
      
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        toast.success(`✅ Coupon applied! ${response.data.discount_type === 'percentage' ? response.data.discount_value + '%' : '$' + (response.data.discount_value / 100).toFixed(2)} off`);
      } else {
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Failed to apply coupon:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to apply coupon');
    } finally {
      setApplyingCoupon(false);
    }
  };
  
  const calculateDiscount = () => {
    if (!appliedCoupon || !cartTotal) return 0;
    const subtotal = cartTotal.subtotal || 0;
    
    if (appliedCoupon.discount_type === 'percentage') {
      return Math.floor((subtotal * appliedCoupon.discount_value) / 100);
    } else if (appliedCoupon.discount_type === 'fixed') {
      return appliedCoupon.discount_value;
    }
    return 0;
  };
  
  // Attributes are now handled at checkout, not in cart
  // Cart redirects to CheckoutSection for payment processing

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="pi pi-shopping-cart text-6xl text-gray-400 mx-auto mb-4 block"></i>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
        <p className="text-gray-600">Add some customised prints to get started</p>
      </div>
    );
  }

  // Check if any cart items are missing their source images
  const itemsMissingImages = items.filter(item => {
    return !uploadedImages.find(img => img.filename === item.filename);
  });

  return (
    <div className="space-y-6">
      {/* Step Navigation */}
      <StepNavigation 
        currentStep="cart"
        onStepChange={setActiveTab}
        canGoNext={groupedItems.length > 0}
        canGoBack={true}
      />
      
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Cart</h2>
        <p className="text-gray-600">Review your print order</p>
      </div>

      {/* Warning for items missing source images */}
      {itemsMissingImages.length > 0 && (
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-start space-x-3">
            <i className="pi pi-exclamation-triangle text-yellow-600 mt-0.5"></i>
            <div className="flex-1">
              <h4 className="font-medium text-yellow-900">Some items cannot be edited</h4>
              <p className="text-sm text-yellow-800 mt-1">
                {itemsMissingImages.length} item(s) in your cart do not have source images loaded. 
                You can still checkout, but you cannot edit these items. 
                To edit them, please re-upload the images or remove these items.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm(`Remove ${itemsMissingImages.length} item(s) without source images?`)) {
                    itemsMissingImages.forEach(item => onRemoveItem(item.id));
                    toast.success(`Removed ${itemsMissingImages.length} item(s) from cart`);
                  }
                }}
                className="mt-2 text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              >
                <i className="pi pi-trash mr-2"></i>
                Remove Items Without Images
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Top Checkout Button */}
      {groupedItems.length > 0 && (
        <Card className="p-6 max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">Ready to checkout?</h3>
              <p className="text-sm text-gray-600">You have {items.length} item(s) in your cart</p>
            </div>
            <Button 
              variant="outline"
              onClick={() => setActiveTab('checkout')}
              className="text-custom-orange hover:text-orange-700 hover:bg-orange-50 h-12 px-8"
              data-testid="checkout-button-top"
            >
              <i className="pi pi-shopping-cart mr-2"></i>
              Proceed to Checkout
              <i className="pi pi-arrow-right ml-2"></i>
            </Button>
          </div>
        </Card>
      )}

      {/* Bulk Discount Applied Notice */}
      {cartTotal?.bulk_discount_applied && (
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center">
            <i className="pi pi-check-circle text-green-600 mr-2"></i>
            <span className="text-green-800 font-medium">Bulk Discount Applied!</span>
          </div>
        </Card>
      )}

      <div className="space-y-6">
        {/* Grouped Cart Items */}
        <div className="space-y-4">
          {/* Sort Controls and Empty Cart Button */}
          {groupedItems.length > 0 && (
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-base border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-custom-orange focus:border-transparent min-w-[160px]"
                  data-testid="cart-sort-select"
                >
                  <option value="added">Order Added</option>
                  <option value="filename-asc">Filename (A-Z)</option>
                  <option value="filename-desc">Filename (Z-A)</option>
                </select>
              </div>
              <Button 
                variant="outline" 
                onClick={() => {
                  if (window.confirm('Are you sure you want to empty your cart? This will remove all items.')) {
                    items.forEach(item => onRemoveItem(item.id));
                    setCartTotal(null);
                    setAppliedCoupon(null);
                    setCouponCode('');
                    toast.success('Cart emptied successfully');
                  }
                }}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="empty-cart-button"
              >
                <i className="pi pi-trash mr-2"></i>
                Empty Cart
              </Button>
            </div>
          )}
          
          {sortedGroupedItems.map((group) => {
            const isExpanded = expandedGroups[group.filename];
            
            return (
              <Card key={group.filename} className="overflow-hidden">
                {/* Group Header - Collapsible */}
                <div 
                  className="animated-border-element p-3 bg-gray-50 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors mb-2"
                  onClick={() => toggleGroup(group.filename)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Image Thumbnail */}
                      <div className="relative w-12 h-12 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                        {group.thumbnail ? (
                          <img 
                            src={`data:image/jpeg;base64,${group.thumbnail}`}
                            alt={group.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Group thumbnail failed to load:', group.filename);
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                            <i className="pi pi-image text-2xl"></i>
                          </div>
                        )}
                      </div>
                      
                      {/* Image Info */}
                      <div>
                        <h3 className="font-medium text-gray-900">{group.filename}</h3>
                        <p className="text-sm text-gray-600">
                          {group.totalItems} item{group.totalItems !== 1 ? 's' : ''} • ${(group.totalPrice / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Expand/Collapse Icon with text */}
                      <span className="text-sm text-gray-600">Review/Edit</span>
                      <i className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'} text-gray-500`}></i>
                    </div>
                  </div>
                </div>
                
                {/* Group Items - Collapsible */}
                {isExpanded && (
                  <div className="divide-y divide-gray-100">
                    {group.items.map((item) => {
                      // Use shared utility to find product config by SKU or size
                      const { config: productConfig, key: productKey } = findProductConfig(item, printSizes);
                      
                      // Calculate display prices
                      const calculatedItem = cartTotal?.items?.find(
                        calcItem => calcItem.filename === item.filename && (calcItem.sku === productKey || calcItem.size === item.size)
                      );
                      const displayUnitPrice = calculatedItem 
                        ? calculatedItem.unit_price 
                        : (productConfig?.price || 299);
                      const displayTotalPrice = calculatedItem 
                        ? calculatedItem.total_price 
                        : displayUnitPrice * item.quantity;

                      return (
                        <div key={item.id} className="p-4">
                          <div className="flex items-center space-x-4">
                            {/* Individual Item Thumbnail - Shows the actual crop for this size */}
                            {(() => {
                              // Check if we have the original image for editing
                              const uploadedImage = uploadedImages.find(img => img.filename === item.filename);
                              const canEdit = uploadedImage?.image_data || uploadedImage?.preview;
                              
                              // Calculate aspect ratio using shared utility
                              const aspectRatioPadding = calculateAspectPadding(productConfig, item.aspectOrientation || 'landscape');
                              
                              if (productConfig?.crop_ratio_x && productConfig?.crop_ratio_y) {
                                console.log(`🛒 Cart: ${productKey} (${productConfig.crop_ratio_x}:${productConfig.crop_ratio_y}) → ${item.aspectOrientation || 'landscape'} → padding=${aspectRatioPadding.toFixed(1)}%`);
                              }
                              
                              return (
                                <div className="flex-shrink-0" style={{ width: '64px' }}>
                                  <div 
                                    className={`animated-border-element relative rounded overflow-hidden bg-gray-200 ${canEdit ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed opacity-75'}`}
                                    style={{ 
                                      width: '100%',
                                      paddingBottom: `${aspectRatioPadding}%`
                                    }}
                                    onClick={async () => {
                                    if (!canEdit) {
                                      toast.warning(`Cannot edit ${item.filename} - original image not loaded. Please go to Edit Images tab to re-upload or edit.`);
                                      return;
                                    }
                                    
                                    // Get ALL cart quantities for this image filename, not just current item
                                    const allCartQuantities = {};
                                    const allCartItemIds = {};
                                    items.filter(cartItem => cartItem.filename === item.filename).forEach(cartItem => {
                                      // Use SKU as the key since printSizes is keyed by SKU
                                      const key = cartItem.sku || cartItem.size;
                                      allCartQuantities[key] = cartItem.quantity;
                                      allCartItemIds[key] = cartItem.id;
                                    });
                                    
                                    // If we have preview in uploadedImages, use it directly
                                    if (uploadedImage?.preview || uploadedImage?.image_data) {
                                      setEditingImage({
                                        isCartEdit: true,
                                        cartItem: {
                                          ...item,
                                          thumbnail: uploadedImage?.thumbnail || uploadedImage?.preview,
                                          image_data: uploadedImage?.image_data || uploadedImage?.preview,
                                          preview: uploadedImage?.preview
                                        },
                                        filename: item.filename,
                                        cartQuantities: allCartQuantities,
                                        cartItemIds: allCartItemIds
                                      });
                                      return;
                                    }
                                    
                                    // Otherwise, fetch the preview from backend API using image_id
                                    if (uploadedImage?.id) {
                                      try {
                                        const response = await axios.get(`/images/${uploadedImage.id}/base64?type=preview`);
                                        if (response.data && response.data.data) {
                                          setEditingImage({
                                            isCartEdit: true,
                                            cartItem: {
                                              ...item,
                                              thumbnail: uploadedImage?.thumbnail,
                                              image_data: response.data.data,
                                              preview: response.data.data
                                            },
                                            filename: item.filename,
                                            cartQuantities: allCartQuantities,
                                            cartItemIds: allCartItemIds
                                          });
                                        }
                                      } catch (error) {
                                        console.error('Failed to fetch preview:', error);
                                        toast.error('Failed to load image for editing');
                                      }
                                    } else {
                                      toast.warning(`Cannot edit ${item.filename} - image ID not found`);
                                    }
                                  }}
                                  title={canEdit ? "Click to edit this specific crop" : "Original image not loaded - cannot edit"}
                                >
                              {/* Display cropped thumbnail with CSS rotation (like OrdersSection) */}
                              {(() => {
                                const uploadedImage = uploadedImages.find(img => img.filename === item.filename);
                                
                                // Get orientation from item
                                const orientation = item.aspectOrientation || 'landscape';
                                
                                // Priority 1: Use cropped thumbnail for this specific size+orientation (by SKU-orientation key)
                                const orientationKey = `${productKey}-${orientation}`;
                                let croppedThumbObj = uploadedImage?.croppedThumbnails?.[orientationKey];
                                
                                // Fallback to just SKU key
                                if (!croppedThumbObj) {
                                  croppedThumbObj = uploadedImage?.croppedThumbnails?.[productKey];
                                }
                                
                                const croppedThumb = typeof croppedThumbObj === 'string' 
                                  ? croppedThumbObj 
                                  : croppedThumbObj?.data;
                                
                                // Check if we have a cropped thumbnail
                                const hasCroppedThumb = croppedThumb && croppedThumb.length > 100;
                                
                                // Priority 2: Fall back to original thumbnail
                                const thumbnailSrc = croppedThumb || uploadedImage?.thumbnail || uploadedImage?.image_data;
                                
                                if (hasCroppedThumb) {
                                  console.log(`🖼️ Cart thumb: ${productKey} using cropped (${orientationKey})`);
                                }
                                
                                return thumbnailSrc ? (
                                  <img 
                                    src={`data:image/jpeg;base64,${thumbnailSrc}`}
                                    alt={`${item.filename} - ${productConfig?.name || item.size}`}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    onError={(e) => {
                                      console.error('Thumbnail failed to load for:', item.filename, productKey);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200 text-gray-400 text-xs text-center p-1">
                                    <div>
                                      <i className="pi pi-image text-lg block mb-1"></i>
                                      {productConfig?.name || item.size}
                                    </div>
                                  </div>
                                );
                              })()}
                                  {/* Visual indicator if cannot edit */}
                                  {!canEdit && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                      <i className="pi pi-lock text-white text-sm"></i>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                            })()}
                            
                            {/* Item Details */}
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {productConfig?.name || item.size}
                                </div>
                                {item.paper_type && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                    {item.paper_type}
                                  </span>
                                )}
                                {item.border && (
                                  <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                                    White Border
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                ${(displayUnitPrice / 100).toFixed(2)} each
                                {calculatedItem?.bulk_discount_applied && (
                                  <span className="text-green-600 ml-2">• Bulk discount applied</span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-4">
                              {/* Inline Quantity Editor */}
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-600">Qty:</label>
                                <QuantityInput
                                  value={item.quantity}
                                  onChange={(newQuantity) => {
                                    if (newQuantity === 0) {
                                      onRemoveItem(item.id);
                                    } else {
                                      onUpdateQuantity(item.id, newQuantity);
                                    }
                                  }}
                                  min={0}
                                  max={999}
                                />
                              </div>
                              
                              {/* Item Total Price */}
                              <div className="text-right min-w-[80px]">
                                <div className="font-medium text-gray-900">
                                  ${(displayTotalPrice / 100).toFixed(2)}
                                </div>
                              </div>
                              
                              {/* Remove Item Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onRemoveItem(item.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Remove this item"
                              >
                                <i className="pi pi-trash"></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
 

        {/* Bottom Checkout Button - Only show when there are 5+ items */}
        {items.length >= 5 && (
          <Card className="p-6 max-w-2xl mx-auto">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to checkout?</h3>
              <p className="text-gray-600 mb-6">Review your order and complete payment on the next page</p>
              <Button 
                variant="outline"
                onClick={() => setActiveTab('checkout')}
                className="w-full text-custom-orange hover:text-orange-700 hover:bg-orange-50 h-12 text-lg"
                data-testid="checkout-button"
              >
                <i className="pi pi-shopping-cart mr-2"></i>
                Proceed to Checkout
                <i className="pi pi-arrow-right ml-2"></i>
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}


export default CartSection;
